import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import stilePulsanti from "../interfaccia/stilePulsanti";
import { BlockSkeleton } from "../interfaccia/loading";
import WebCategoryAttributeRulesPage from "./WebCategoryAttributeRulesPage";
import useCategoryMappingAuth from "./useCategoryMappingAuth";
import { canEditProtectedMapping, CATEGORY_MODA_KEYS, conflictsFromVerification } from "./categoryDefaultConflicts";
import { isCancelledRead, loadDefaultConflicts, verifyDefaultConflict } from "./defaultConflictsApi";

export default function WebCategoryDefaultConflictsPage({ utente, temaColori }) {
  const getAuth = useCategoryMappingAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const combination = searchParams.get("combination") || "";
  const [site, setSite] = useState("");
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [verification, setVerification] = useState(null);
  const [error, setError] = useState("");
  const [editingRuleId, setEditingRuleId] = useState(null);
  const listRead = useRef(null);
  const verifyRead = useRef(null);
  const { buttonClassesGreen, buttonClassesWhite } = stilePulsanti;
  const canManage = Boolean(utente?.permessiAPI?.["update:prestashop"]);
  const validUsername = Boolean(utente?.username?.trim()) && !/^[A-Za-z0-9._-]+\|/.test(utente.username);

  const load = useCallback(async () => {
    listRead.current?.abort();
    verifyRead.current?.abort();
    const controller = new AbortController();
    listRead.current = controller;
    const { signal } = controller;
    setLoading(true);
    setItems([]);
    setError("");
    setVerification(null);
    try {
      const nextItems = await loadDefaultConflicts(getAuth, signal, site);
      if (signal.aborted) return;
      setItems(nextItems);
      setIndex(0);
    } catch (loadError) {
      if (!isCancelledRead(loadError, signal)) setError(loadError?.response?.data?.error || loadError.message);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [getAuth, site]);

  useEffect(() => {
    load();
    return () => { listRead.current?.abort(); verifyRead.current?.abort(); };
  }, [load]);

  const selectedItems = useMemo(() => combination ? items.filter((item) => item.key === combination) : items, [combination, items]);
  useEffect(() => setIndex(0), [combination]);
  const current = selectedItems[index];
  const check = useCallback(async () => {
    verifyRead.current?.abort();
    if (!current) return;
    const controller = new AbortController();
    verifyRead.current = controller;
    const { signal } = controller;
    setChecking(true);
    setVerification(null);
    setError("");
    try {
      const result = await verifyDefaultConflict(current.moda, getAuth, signal);
      if (!signal.aborted) setVerification(result);
    } catch (checkError) {
      if (!isCancelledRead(checkError, signal)) setError(checkError?.response?.data?.error || checkError.message);
    } finally {
      if (!signal.aborted) setChecking(false);
    }
  }, [current, getAuth]);

  useEffect(() => {
    setVerification(null);
    if (!loading) check();
    return () => verifyRead.current?.abort();
  }, [check, loading]);

  const next = () => {
    verifyRead.current?.abort();
    setVerification(null);
    setIndex((value) => value + 1);
  };
  const conflicts = verification ? conflictsFromVerification(verification) : current?.defaultConflicts || {};
  const resolved = verification?.resolved && !verification?.truncated;

  return <div className="pa3">
    <div className="sfondoBiancoTema pa3 br3">
      <div className="flex flex-wrap items-center justify-between mb3">
        <h2 className="ma0">Conflitti default</h2>
        <div className="flex flex-wrap items-center">
          <label className="mr2">Sito <select aria-label="Sito" value={site} disabled={Boolean(editingRuleId)} onChange={(event) => setSite(event.target.value)}>
            <option value="">Tutti</option><option value="VIG">VIG</option><option value="BOT">BOT</option>
          </select></label>
          <button type="button" className={buttonClassesWhite} disabled={loading || Boolean(editingRuleId)} onClick={load}>Aggiorna elenco</button>
        </div>
      </div>
      <p>Correggi le regole di origine: il resolver non sceglie automaticamente un default.</p>
      {combination && <div className="mb3">Combinazione selezionata: {combination} <button type="button" className={buttonClassesWhite} onClick={() => { setSearchParams({}); setIndex(0); }}>Mostra tutti i conflitti</button></div>}
      {!canManage && <p>Modalita lettura: per modificare serve update:prestashop.</p>}
      {canManage && !validUsername && <p className="scrittaRosso">Username utente mancante o non valido: aggiorna la sessione prima di modificare.</p>}
      {error && <div className="scrittaRosso mb3" role="alert">{error}</div>}
      {loading ? <BlockSkeleton height="22rem" /> : !current ? <p>{index > 0 ? "Elenco completato. Aggiorna per ricontrollare i conflitti rimasti." : "Nessun conflitto da mostrare per la selezione corrente."}</p> : <>
        <div className="fw6 mb2">{index + 1} di {selectedItems.length}</div>
        <div className="f5 mb2">{CATEGORY_MODA_KEYS.map((key) => current.moda[key] || "·").join(" / ")}</div>
        <p>Articoli: {current.articleCount} · Siti segnalati: {current.sites.join(", ")} · Checkpoint bloccati VIG→BOT: {current.blockedCheckpoints.VIG_TO_BOT} · BOT→VIG: {current.blockedCheckpoints.BOT_TO_VIG}</p>
        <p className="f7">Le segnalazioni includono i checkpoint con condizioni Mongo. La verifica legge al massimo 1000 articoli non annullati; i conteggi checkpoint sono quelli dell’elenco caricato e la modifica non li rimette in coda automaticamente.</p>
        {checking && <BlockSkeleton height="12rem" />}
        {!checking && verification && <div role="status" className={resolved ? "scrittaVerde fw6 mb3" : "scrittaRosso fw6 mb3"}>
          {resolved ? "Conflitto risolto." : verification.resolved ? "Nessun conflitto negli articoli valutati; verifica incompleta." : "Conflitto ancora presente."}
          {` Verificati ${verification.evaluatedArticles} di ${verification.articleCount} articoli.`}
          {verification.truncated && " Limite di 1000 articoli raggiunto: non e possibile confermare la risoluzione completa."}
        </div>}
        {!checking && Object.entries(conflicts).map(([conflictSite, conflict]) => <section key={conflictSite} className="ba bordoColAlt br2 pa3 mb3">
          <h3 className="mt0 scrittaRosso">{conflictSite}: Default doppio</h3>
          {verification && <p>Articoli ancora ambigui: {verification.sites[conflictSite].ambiguousArticles}</p>}
          {verification?.sites[conflictSite]?.groups.map((group, groupIndex) => <p key={groupIndex} className="f7">
            Default {group.categoryIds.join(", ")} · {group.articleCount} articoli · Esempi: {group.sampleCodVS.join(", ")}
          </p>)}
          {Array.from(new Map(conflict.candidates.map((candidate) => [`${candidate.ruleId}-${candidate.categoryId}`, candidate])).values()).map((candidate) => {
            const editable = canManage && validUsername && canEditProtectedMapping(candidate, utente?.username);
            return <div key={`${candidate.ruleId}-${candidate.categoryId}`} className="ba bordoColAlt br2 pa2 mb2">
              <div className="fw6">{candidate.categoryId} — {candidate.categoryPath || candidate.categoryLabel || "Categoria senza descrizione"}</div>
              <div>Regola #{candidate.ruleId}: {candidate.ruleDescription || candidate.ruleKey || "Senza nome"}</div>
              <div className="f7 mv1">Autore: {candidate.createdBy || "—"} · Ultima modifica: {candidate.updatedBy || "—"} · Specificita: {candidate.specificity === "specific" ? "specifica" : "ereditata"} · Priorita regola: {candidate.rulePriority} · Priorita output: {candidate.outputPriority}</div>
              <div className="f7 mb2">{candidate.protected ? `🔒 Regola protetta — ${candidate.createdBy || "—"} / ${candidate.updatedBy || "—"}${editable ? "" : ": modifica riservata al proprietario"}` : "Regola protetta: no"}</div>
              <button type="button" className={buttonClassesWhite} disabled={!editable || Boolean(editingRuleId)} onClick={() => setEditingRuleId(candidate.ruleId)}>Modifica regola #{candidate.ruleId}</button>
            </div>;
          })}
        </section>)}
        <div className="flex flex-wrap items-center">
          <button type="button" className={buttonClassesWhite + " mr2 mb2"} disabled={checking || Boolean(editingRuleId)} onClick={check}>Ricontrolla conflitto</button>
          <button type="button" className={buttonClassesWhite + " mr2 mb2"} disabled={Boolean(editingRuleId)} onClick={next}>Salta</button>
          <button type="button" className={buttonClassesGreen + " mb2"} disabled={!resolved || checking || Boolean(editingRuleId)} onClick={next}>Prossimo conflitto</button>
        </div>
      </>}
    </div>
    {editingRuleId && <WebCategoryAttributeRulesPage editorOnly editRuleId={editingRuleId} utente={utente} temaColori={temaColori}
      onEditorClose={() => setEditingRuleId(null)}
      onRuleSaved={async () => { setEditingRuleId(null); await check(); }} />}
  </div>;
}
